<template>
  <div v-if="hasAlerts" class="widget">
    <div class="widget-heading">
      <h2 class="textStyle">{{ $t("registration.follow") }}</h2>
      <div class="filters">
        <button
          v-for="level in alertLevels"
          :key="level.value"
          :class="['filter', `-${level.type}`, { '-active': currentLevel === level.value }]"
          @click="toggleFilter(level.value)"
        >
          {{ level.count }}
        </button>
      </div>
    </div>

    <div class="widget-content">
      <template v-if="displayedAlerts.length">
        <FormAlertComponent v-for="alert in displayedAlerts" :key="alert.id" :alert="alert" type="minimal" />
      </template>
      <div v-else>
        {{ $tc("registration.alert.empty", { level: currentLevelLabel }) }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";
import { useRegistrationStore } from "@/store/registration/registration";
import { i18n } from "@/i18n";
import FormAlertComponent from "@/components/form/alert/FormAlertComponent.vue";

const ALERT_LEVELS = {
  INFO: 1,
  WARNING: 2,
  DANGER: 3,
};

const LEVEL_TRANSLATIONS = {
  [ALERT_LEVELS.INFO]: "registration.alert.level.info",
  [ALERT_LEVELS.WARNING]: "registration.alert.level.warning",
  [ALERT_LEVELS.DANGER]: "registration.alert.level.error",
};

export default {
  name: "RegistrationHelpComponent",
  components: { FormAlertComponent },

  data() {
    return {
      currentLevel: null,
    };
  },

  computed: {
    ...mapState(useRegistrationStore, {
      alerts: (store) => store.alerts,
    }),

    hasAlerts() {
      return this.alerts && Object.keys(this.alerts).length > 0;
    },

    triggeredAlerts() {
      return this.alerts.filter((alert) => alert.isTriggered);
    },

    alertLevels() {
      return [
        {
          value: ALERT_LEVELS.DANGER,
          type: "danger",
          count: this.getAlertCountByLevel(ALERT_LEVELS.DANGER),
        },
        {
          value: ALERT_LEVELS.WARNING,
          type: "warning",
          count: this.getAlertCountByLevel(ALERT_LEVELS.WARNING),
        },
        {
          value: ALERT_LEVELS.INFO,
          type: "info",
          count: this.getAlertCountByLevel(ALERT_LEVELS.INFO),
        },
      ];
    },

    displayedAlerts() {
      if (this.currentLevel === null) {
        return this.triggeredAlerts;
      }
      return this.triggeredAlerts.filter((alert) => alert.level === this.currentLevel);
    },

    currentLevelLabel() {
      if (this.currentLevel === null) {
        return "";
      }
      const translationKey = LEVEL_TRANSLATIONS[this.currentLevel];
      return i18n.global.t(translationKey).toLowerCase();
    },
  },

  methods: {
    getAlertCountByLevel(level) {
      return this.alerts.filter((alert) => alert.level === level).length;
    },

    toggleFilter(level) {
      this.currentLevel = this.currentLevel === level ? null : level;
    },
  },
};
</script>

<style lang="scss" scoped>
.alert {
  background: none;
  border: none;
}

.widget-heading {
  flex-direction: column;
}

.filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  gap: 8px;

  .filter {
    flex-grow: 1;
    padding: 5px 0;
    border-radius: $radius;
    border: none;
    font-weight: 700;
    position: relative;

    &.-active::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      border: 2px solid;
      width: 100%;
      height: 100%;
      border-radius: $radius;
    }

    &.-info {
      background: $light-purple;
      color: $purple;

      &.-active::after {
        border-color: $purple;
      }
    }

    &.-warning {
      background: $light-orange;
      color: $orange;

      &.-active::after {
        border-color: $orange;
      }
    }

    &.-danger {
      background: $light-red;
      color: $red;

      &.-active::after {
        border-color: $red;
      }
    }
  }
}
</style>
